import { type FormEvent, useEffect, useState } from "react";
import {
  type Account,
  type AccountBalance,
  type AccountKind,
  ApiError,
  type BalanceSummary,
  type Contact,
  type OwnerKind,
  type Person,
  accountKindLabel,
  api,
  formatMoney,
} from "./api";
import { DateField, SelectField } from "./fields";
import { PageHeader } from "./layout/Layout";
import { Till } from "./Till";
import { Alert, Badge, Box, Empty, Field, Loading, Modal, ModalActions, StatCard, Table, Th, TwoLine } from "./ui";

const START_DATE = "2026-06-24";

const KIND_ICONS: Record<AccountKind, string> = {
  bank: "ri-bank-line",
  cash: "ri-money-dollar-circle-line",
  card: "ri-bank-card-line",
  crypto: "ri-coin-line",
};

/**
 * Accounts and suppliers (quotation A4), with the balance check (A8) across the top.
 *
 * Accounts are grouped by who owns them, because that grouping is the whole of the two
 * sets of books: the company's money first, then each person's own. A person only ever
 * sees their own here, and the masters see everybody's.
 */
export function Accounts({ canManage }: { readonly canManage: boolean }): React.ReactElement {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [balances, setBalances] = useState<BalanceSummary | undefined>();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [adding, setAdding] = useState<"account" | "contact" | undefined>();

  async function reload(): Promise<void> {
    try {
      const [accountsResult, balancesResult, contactsResult, peopleResult] = await Promise.all([
        api.accounts(),
        api.balances(),
        api.contacts(),
        api.people(),
      ]);
      setAccounts(accountsResult.accounts);
      setBalances(balancesResult);
      setContacts(contactsResult.contacts);
      setPeople(peopleResult.people);
      setError(undefined);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load financial accounts");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  if (loading) return <Loading />;

  // The company first, then each person in the order the server sends them.
  const owners = [...new Set(accounts.map((account) => account.ownerName))].sort((a, b) => {
    const aCompany = accounts.some((account) => account.ownerName === a && account.ownerKind === "company");
    const bCompany = accounts.some((account) => account.ownerName === b && account.ownerKind === "company");
    return Number(bCompany) - Number(aCompany);
  });
  const balanceOf = (id: string): AccountBalance | undefined =>
    balances?.accounts.find((entry) => entry.accountId === id);
  const afterSave = (): void => {
    setAdding(undefined);
    void reload();
  };

  return (
    <>
      <PageHeader
        title="Financial accounts"
        subtitle="Bank, cash and wallet accounts, contacts and cash closing. A personal account holds that person's own funds."
        actions={
          canManage ? (
            <>
              <button type="button" className="ti-btn ti-btn-primary-full" onClick={() => setAdding("account")}>
                <i className="ri-add-line" /> Add financial account
              </button>
              <button type="button" className="ti-btn ti-btn-light" onClick={() => setAdding("contact")}>
                <i className="ri-user-add-line" /> Add supplier or customer
              </button>
            </>
          ) : undefined
        }
      />

      {error ? <Alert tone="danger">{error}</Alert> : null}

      {balances ? <BalanceCheck summary={balances} /> : null}

      <Till accounts={accounts} canBank={canManage} onChanged={() => void reload()} />

      {accounts.length === 0 ? (
        <Box title="Financial accounts">
          <Empty icon="ri-bank-line">No financial accounts have been created.</Empty>
        </Box>
      ) : (
        owners.map((owner) => {
          const owned = accounts.filter((account) => account.ownerName === owner);
          const company = owned.some((account) => account.ownerKind === "company");
          return (
            <Box
              key={owner}
              title={company ? "Company accounts" : `${owner} - personal accounts`}
              count={owned.length}
            >
              <Table
                head={
                  <>
                    <Th>Account</Th>
                    <Th end>Recorded account balance</Th>
                    <Th>Bank statement</Th>
                    <Th end>Reconciliation status</Th>
                  </>
                }
              >
                {owned.map((account) => {
                  const balance = balanceOf(account.id);
                  const gap = balance?.differenceCents ?? null;
                  return (
                    <tr key={account.id} className="border-b border-defaultborder">
                      <td>
                        <div className="flex items-center">
                          <span className="avatar avatar-sm avatar-rounded bg-light !text-[#8c9097] dark:!text-white/50 me-2 !mb-0">
                            <i className={`${KIND_ICONS[account.kind]} text-[1rem]`} />
                          </span>
                          <TwoLine
                            main={account.name}
                            sub={[accountKindLabel(account.kind), account.institution].filter(Boolean).join(" · ")}
                          />
                        </div>
                      </td>
                      <td className="!text-end">
                        <TwoLine
                          main={`${account.currency} ${formatMoney(balance?.ourBalanceCents ?? account.openingBalanceCents)}`}
                          sub={
                            gap !== null && gap !== 0 && balance?.bankBalanceCents != null
                              ? `Statement balance ${formatMoney(balance.bankBalanceCents)}`
                              : undefined
                          }
                        />
                      </td>
                      <td className="text-[#8c9097] dark:text-white/50">
                        {balance && balance.lineCount > 0 ? (
                          <TwoLine
                            main={`${balance.lineCount} transactions to ${balance.lastLineDate}`}
                            sub={balance.unmatchedCount > 0 ? `${balance.unmatchedCount} unmatched` : "All matched"}
                          />
                        ) : (
                          `Opened ${account.openingDate}, no statement imported`
                        )}
                      </td>
                      <td className="!text-end">
                        {!account.isActive ? (
                          <Badge tone="light">Closed</Badge>
                        ) : gap === 0 ? (
                          <Badge tone="success">Balanced</Badge>
                        ) : gap !== null ? (
                          <Badge tone="danger">Difference {formatMoney(Math.abs(gap))}</Badge>
                        ) : (
                          <Badge tone="light">Not reconciled</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </Table>
            </Box>
          );
        })
      )}

      <Box title="Suppliers and customers" count={contacts.length}>
        {contacts.length === 0 ? (
          <Empty icon="ri-contacts-book-line">No suppliers or customers have been added.</Empty>
        ) : (
          <Table
            head={
              <>
                <Th>Name</Th>
                <Th>Relationship</Th>
                <Th>Phone</Th>
                <Th>UEN</Th>
              </>
            }
          >
            {contacts.map((contact) => (
              <tr key={contact.id} className="border-b border-defaultborder">
                <td>
                  <TwoLine main={contact.name} sub={contact.isCompany ? "Company" : "Individual"} />
                </td>
                <td>
                  {contact.kind === "supplier" ? (
                    <Badge tone="info">Supplier</Badge>
                  ) : contact.kind === "customer" ? (
                    <Badge tone="success">Customer</Badge>
                  ) : (
                    <Badge tone="primary">Supplier and customer</Badge>
                  )}
                </td>
                <td className="!whitespace-normal">
                  {contact.phone ?? ""}
                  {/* The rest of what its invoices say (D, 11 October 2026). */}
                  {[contact.email, contact.website, contact.address].filter(Boolean).map((text) => (
                    <div key={text} className="text-[0.75rem] text-[#8c9097] dark:text-white/50">{text}</div>
                  ))}
                </td>
                <td className="text-[#8c9097] dark:text-white/50">{contact.uen ?? ""}</td>
              </tr>
            ))}
          </Table>
        )}
      </Box>

      <AddAccount people={people} open={adding === "account"} onClose={() => setAdding(undefined)} onDone={afterSave} />
      <AddContact open={adding === "contact"} onClose={() => setAdding(undefined)} onDone={afterSave} />
    </>
  );
}

/**
 * Balance check (quotation A8).
 *
 * Our figure against the bank's, and the gap named rather than smoothed over. A system
 * that reports its own arithmetic as the truth is how a missing transaction survives
 * for months, so when the two do not agree the screen says so first.
 */
function BalanceCheck({ summary }: { readonly summary: BalanceSummary }): React.ReactElement {
  const hasStatements = summary.accounts.some((account) => account.lineCount > 0);
  const totals = summary.companyTotals.length ? summary.companyTotals : [{ currency: "SGD", totalCents: 0 }];

  return (
    <>
      {summary.outOfBalance.length > 0 ? (
        <Alert tone="danger">
          {summary.outOfBalance.map((account) => (
            <div key={account.accountId}>
              <span className="font-semibold">{account.accountName}</span> is{" "}
              {formatMoney(Math.abs(account.differenceCents ?? 0))}{" "}
              {(account.differenceCents ?? 0) > 0 ? "more" : "less"} than the statement balance.
            </div>
          ))}
        </Alert>
      ) : !hasStatements ? (
        <Alert tone="info">
          No bank statement has been imported yet, so these figures show opening balances only. Once the
          OCBC statements are imported, each recorded account balance is compared with the statement balance.
        </Alert>
      ) : (
        <Alert tone="success">All account balances match their bank statements.</Alert>
      )}

      <div className="grid grid-cols-12 gap-x-6">
        {totals.map((total) => (
          <div key={total.currency} className="xl:col-span-4 md:col-span-6 col-span-12">
            <StatCard
              label={`Company funds (${total.currency})`}
              value={formatMoney(total.totalCents)}
              icon="ti ti-building-bank"
              tone="primary"
              note="All company accounts combined"
            />
          </div>
        ))}
        <div className="xl:col-span-4 md:col-span-6 col-span-12">
          <StatCard
            label="Unmatched bank transactions"
            value={summary.unmatchedTotal}
            icon="ti ti-arrows-exchange"
            tone={summary.unmatchedTotal ? "warning" : "success"}
            note={summary.unmatchedTotal ? "Each needs a category or payment voucher" : "All bank transactions matched"}
          />
        </div>
        <div className="xl:col-span-4 md:col-span-6 col-span-12">
          <StatCard
            label="Accounts with balance differences"
            value={summary.outOfBalance.length}
            icon="ti ti-scale"
            tone={summary.outOfBalance.length ? "danger" : "success"}
            note={summary.outOfBalance.length ? "Listed in red above" : "Recorded balances match the bank"}
            {...(summary.outOfBalance.length ? { noteTone: "danger" as const } : {})}
          />
        </div>
      </div>
    </>
  );
}

function AddAccount({
  people,
  open,
  onClose,
  onDone,
}: {
  readonly people: readonly Person[];
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [ownerKind, setOwnerKind] = useState<OwnerKind>("company");
  const [ownerPersonId, setOwnerPersonId] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<AccountKind>("bank");
  const [institution, setInstitution] = useState("");
  const [currency, setCurrency] = useState("SGD");
  const [openingBalance, setOpeningBalance] = useState("");
  const [openingDate, setOpeningDate] = useState(START_DATE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await api.addAccount({
        ownerKind,
        ownerPersonId: ownerKind === "person" ? ownerPersonId : null,
        name,
        kind,
        currency,
        institution: institution || null,
        openingBalance,
        openingDate,
      });
      setName("");
      setInstitution("");
      setOpeningBalance("");
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not add the account");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Add financial account"
      onClose={onClose}
      footer={<ModalActions busy={busy} label="Save account" onCancel={onClose} onSave={() => void submit()} />}
    >
      <form className="grid grid-cols-12 gap-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        <Field
          id="owner"
          label="Account owner"
          className="col-span-12"
          hint={
            ownerKind === "company"
              ? "Company funds. Purchases paid from this account become company inventory."
              : "The person's own funds. Purchases paid from this account remain personally funded inventory until the company buys them."
          }
        >
          <SelectField
            id="owner"
            value={ownerKind === "company" ? "company" : ownerPersonId}
            options={[
              { value: "company", label: "Company" },
              ...people.map((person) => ({ value: person.id, label: person.fullName })),
            ]}
            onChange={(choice) => {
              if (choice === "company") {
                setOwnerKind("company");
                setOwnerPersonId("");
              } else {
                setOwnerKind("person");
                setOwnerPersonId(choice);
              }
            }}
          />
        </Field>
        <Field id="account-name" label="Account name" className="sm:col-span-7 col-span-12">
          <input
            id="account-name"
            type="text"
            className="form-control"
            value={name}
            placeholder="OCBC Business"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field id="account-kind" label="Account type" className="sm:col-span-5 col-span-12">
          <SelectField<AccountKind>
            id="account-kind"
            value={kind}
            options={[
              { value: "bank", label: "Bank" },
              { value: "cash", label: "Cash" },
              { value: "card", label: "Card" },
              { value: "crypto", label: "Crypto" },
            ]}
            onChange={setKind}
          />
        </Field>
        <Field id="institution" label="Bank or wallet provider" className="sm:col-span-7 col-span-12">
          <input
            id="institution"
            type="text"
            className="form-control"
            value={institution}
            placeholder="OCBC"
            onChange={(event) => setInstitution(event.target.value)}
          />
        </Field>
        <Field id="currency" label="Currency" className="sm:col-span-5 col-span-12">
          <input
            id="currency"
            type="text"
            className="form-control"
            value={currency}
            maxLength={3}
            onChange={(event) => setCurrency(event.target.value.toUpperCase())}
          />
        </Field>
        <Field id="opening-date" label="Opening date" className="sm:col-span-6 col-span-12">
          <DateField id="opening-date" value={openingDate} onChange={setOpeningDate} />
        </Field>
        <Field id="opening-balance" label="Opening balance" className="sm:col-span-6 col-span-12" hint="Leave empty for zero.">
          <input
            id="opening-balance"
            type="text"
            className="form-control"
            value={openingBalance}
            placeholder="0.00"
            inputMode="decimal"
            onChange={(event) => setOpeningBalance(event.target.value)}
          />
        </Field>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}

function AddContact({
  open,
  onClose,
  onDone,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"supplier" | "customer" | "both">("supplier");
  const [isCompany, setIsCompany] = useState(true);
  const [phone, setPhone] = useState("");
  const [uen, setUen] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await api.addContact({ name, kind, isCompany, phone: phone || null, uen: uen || null });
      setName("");
      setPhone("");
      setUen("");
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not add the contact");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Add supplier or customer"
      onClose={onClose}
      footer={<ModalActions busy={busy} label="Save contact" onCancel={onClose} onSave={() => void submit()} />}
    >
      <form className="grid grid-cols-12 gap-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        <Field id="contact-name" label="Name" className="col-span-12">
          <input
            id="contact-name"
            type="text"
            className="form-control"
            value={name}
            placeholder="Bulbasaur Bunker"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field id="contact-kind" label="Relationship" className="sm:col-span-6 col-span-12">
          <SelectField<typeof kind>
            id="contact-kind"
            value={kind}
            options={[
              { value: "supplier", label: "Supplier" },
              { value: "customer", label: "Customer" },
              { value: "both", label: "Supplier and customer" },
            ]}
            onChange={setKind}
          />
        </Field>
        <Field id="contact-is-company" label="Contact type" className="sm:col-span-6 col-span-12">
          <SelectField<"company" | "person">
            id="contact-is-company"
            value={isCompany ? "company" : "person"}
            options={[
              { value: "company", label: "Company" },
              { value: "person", label: "Individual" },
            ]}
            onChange={(choice) => setIsCompany(choice === "company")}
          />
        </Field>
        <Field id="contact-phone" label="Phone" className="sm:col-span-6 col-span-12" hint="Optional.">
          <input
            id="contact-phone"
            type="text"
            className="form-control"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </Field>
        <Field id="contact-uen" label="Unique Entity Number (UEN)" className="sm:col-span-6 col-span-12" hint="Optional.">
          <input
            id="contact-uen"
            type="text"
            className="form-control"
            value={uen}
            onChange={(event) => setUen(event.target.value)}
          />
        </Field>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}
